import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { setNewTaskVariables } from "../../../actions/task";
import { useWorkflowContext } from "../../../components/Workflow/WorkflowContextProvider";
import { icon, quote } from "../../../config";
import { useEditorContext } from "../../editor.Context";
import { customVariablesClasses, generateUniqueId, smartChipClasses } from "../../editor.helper";
import "../taskDetail.scss";
import { useTaskVariableUpdates, useWorkflowVariableUpdates } from "./hooks/taskCustomVariable.hooks";
import { buildTaskCustomVariablePayloads, findExistingTaskCustomVariable, resolveTaskVariableDetails } from "./taskCustomVariable.utils";

/**
 * used to render number type component in task description variable
 * @author {Prachi Jain}
 */
const CustomVariableNumbers = ({ updateNonEditable, variableDetails, smartChipId, setShowEditChip, editorRef }) => {
  const [selectedValue, setSelectedValue] = useState(null);
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { isNewTask, task, workflowCustomVariables } = useSelector((state) => state.taskSidebar);
  const [warning, setWarning] = useState("");
  const location = useLocation();
  const { taskCustomVariables, setTaskCustomVariables, taskCustomVariablesRef } = useEditorContext();
  const { addTaskCustomVariables, addWorklowDescriptionCustomVariables } = useTaskVariableUpdates();
  const { updateSmartWorkflowVariables } = useWorkflowVariableUpdates();

  const workflowState = useWorkflowContext();

  const exisitingVariable = useMemo(
    () =>
      findExistingTaskCustomVariable({
        smartChipId,
        taskCustomVariables,
        workflowContextId: workflowState?.workflow?.Id,
      }),
    [smartChipId, taskCustomVariables, workflowState?.workflow?.Id]
  );

  const newVariableDetails = useMemo(
    () => resolveTaskVariableDetails(variableDetails, exisitingVariable, workflowCustomVariables),
    [variableDetails, exisitingVariable, workflowCustomVariables]
  );

  const { min, max, onlyPositiveNumbers, decimal } = newVariableDetails?.attributes || {};

  const minProps = useMemo(() => {
    return onlyPositiveNumbers ? { min: min ? Math.max(min, 0) : 0 } : typeof min === "number" ? { min } : {};
  }, [onlyPositiveNumbers, min]);

  const maxProps = useMemo(() => {
    return typeof max === "number" ? { max } : {};
  }, [max]);

  const formatProps = useMemo(() => {
    return decimal ? { format: "n2" } : {};
  }, [decimal]);

  useEffect(() => {
    if (smartChipId) return setSelectedValue(exisitingVariable?.variableValue);
    setSelectedValue(newVariableDetails?.attributes?.defaultValue);
  }, [smartChipId, newVariableDetails?.attributes]);

  const inputChangeHandler = (event) => {
    const chosenValue = event.target.value;
    if (typeof newVariableDetails?.attributes?.max === "number" && chosenValue > newVariableDetails?.attributes?.max) {
      setWarning(quote.MAX_LIMIT_REACHED);
    } else {
      setWarning("");
    }
    const finalValue = decimal || chosenValue === null ? chosenValue : parseInt(chosenValue);
    setSelectedValue(finalValue);
  };

  const updateNode = () => {
    const chipClass = newVariableDetails?.chipType === "smart chip" ? smartChipClasses[4] : customVariablesClasses[4];
    if (smartChipId) {
      const updateChip = editorRef?.current?.element?.querySelector(`#${smartChipId}`);
      updateChip.innerHTML = selectedValue;
      setShowEditChip(false);
      const taskCustomVariable = { ...exisitingVariable, variableValue: selectedValue };

      const newTaskCustomVariables = taskCustomVariables.map((item) => (item.taskVariableId === smartChipId ? { ...taskCustomVariable } : item));
      taskCustomVariablesRef.current = [...newTaskCustomVariables];
      setTaskCustomVariables([...newTaskCustomVariables]);
      updateSmartWorkflowVariables([...newTaskCustomVariables]);
      if (!isNewTask) addTaskCustomVariables([...newTaskCustomVariables]);
      else dispatch(setNewTaskVariables([...newTaskCustomVariables]));
    }

    if (!smartChipId) {
      const sanitizedValue = decimal ? selectedValue?.toString()?.replace(/\./g, "_") : selectedValue;
      const id = generateUniqueId(user.id, "customNumber", workflowState?.workflow?.Id ? variableDetails?.attributes?.defaultValue : sanitizedValue);
      updateNonEditable(workflowState?.workflow?.Id ? variableDetails?.name : selectedValue?.toString(), id, chipClass);
      /** works only when workflow page is opened but task sidebar is not open */
      if (location?.pathname?.includes("/workflow") && !isNewTask && !task?.taskId) {
        const { newWorkflowCustomVariable: taskCustomVariable } = buildTaskCustomVariablePayloads({
          variableDetails,
          userId: user.id,
          variableValue: variableDetails?.attributes?.defaultValue,
          generatedId: id,
        });
        taskCustomVariablesRef.current = [...taskCustomVariables, taskCustomVariable];
        setTaskCustomVariables([...taskCustomVariables, taskCustomVariable]);
        updateSmartWorkflowVariables([...taskCustomVariables, taskCustomVariable]);
        if (workflowState?.workflow?.Id) addWorklowDescriptionCustomVariables([...taskCustomVariables, taskCustomVariable], workflowState?.workflow);
        else workflowState?.setworkflowDescriptionVariables([...taskCustomVariables, taskCustomVariable]);
      } else {
        const { newTaskCustomVariable: taskCustomVariable } = buildTaskCustomVariablePayloads({
          variableDetails,
          userId: user.id,
          variableValue: selectedValue,
          generatedId: id,
        });
        taskCustomVariablesRef.current = [...taskCustomVariables, taskCustomVariable];
        setTaskCustomVariables([...taskCustomVariables, taskCustomVariable]);
        updateSmartWorkflowVariables([...taskCustomVariables, taskCustomVariable]);
        if (!isNewTask) addTaskCustomVariables([...taskCustomVariables, taskCustomVariable]);
        else dispatch(setNewTaskVariables([...taskCustomVariables, taskCustomVariable]));
      }
    }
  };

  return (
    <div>
      {!!(workflowState?.workflow?.Id && !smartChipId) && updateNode()}
      {!workflowState?.workflow?.Id && (
        <div style={{ minWidth: "200px" }} className='dt-editor-menu'>
          <div className='w-100' id={`update-editor-custom-number-chip`}>
            <div className='d-flex justify-content-between p-2 pb-0'>
              <div className='font-weight-bold'>{newVariableDetails?.name}</div>
            </div>

            <div className='d-flex gap-2 justify-content-between align-items-center p-2'>
              <NumericTextBox
                type='number'
                rangeOnEnter={true}
                value={selectedValue}
                onChange={inputChangeHandler}
                className='w-100 form-control'
                placeholder={newVariableDetails?.attributes?.placeholder}
                required={newVariableDetails?.attributes?.required}
                step={decimal ? 0.1 : 1}
                {...minProps}
                {...maxProps}
                {...formatProps}
                autoFocus
              />
              <button
                className='square-icon square-icon-extra-small ml-1'
                disabled={
                  (typeof min === "number" && selectedValue < min) || (typeof max === "number" && selectedValue > max) || selectedValue === null
                }
                onClick={updateNode}>
                {icon.CORRECT}
              </button>
            </div>
            {warning && <p className='text-danger ml-1 p-1'>{warning}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomVariableNumbers;
